import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  usePublicTemporaryAccessCancel,
  usePublicTemporaryAccessDecision,
  useSubmitTemporaryAccessCancel,
  useSubmitTemporaryAccessDecision,
} from "hooks/useTemporaryDealerAccess";
import { TemporaryAccessStatus } from "types/temporaryAccess.types";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";
import {
  ActionButton,
  Actions,
  Card,
  Confirmation,
  DetailRow,
  Details,
  HeaderLogo,
  Hint,
  Message,
  PageContainer,
  PageFooter,
  PageHeader,
  PageMain,
  ProcessingState,
  StatusIcon,
  StatusTone,
  Title,
} from "styles/temporaryAccess.styles";

type Mode = "decision" | "cancel";
type DecisionAction = "approve" | "reject";
type Locale = "en" | "fr";
type DealerMessage = (dealer: React.ReactNode) => React.ReactNode;

interface TemporaryAccessDecisionProps {
  mode: Mode;
}

interface CompletedResult {
  status: TemporaryAccessStatus;
  accessExpiresAt?: string | null;
}

interface Copy {
  processing: string;
  processingShort: string;
  dealerFallback: string;
  device: string;
  accessEnds: string;
  approve: string;
  reject: string;
  confirm: string;
  back: string;
  retry: string;
  cancelAccess: string;
  confirmApprove: string;
  confirmReject: string;
  confirmCancel: string;
  pendingTitle: string;
  pendingMessage: DealerMessage;
  cancelTitle: string;
  cancelMessage: DealerMessage;
  approvedTitle: string;
  alreadyApprovedTitle: string;
  approvedMessage: DealerMessage;
  approvedHint: string;
  declinedTitle: string;
  declinedMessage: DealerMessage;
  alreadyDeclinedTitle: string;
  alreadyDeclinedMessage: DealerMessage;
  requestExpiredTitle: string;
  requestExpiredMessage: DealerMessage;
  cancelledTitle: string;
  cancelledMessage: string;
  revokedTitle: string;
  revokedMessage: DealerMessage;
  accessExpiredTitle: string;
  accessExpiredMessage: DealerMessage;
  invalidTitle: string;
  invalidMessage: string;
  errorTitle: string;
  errorMessage: string;
}

const copy: Record<Locale, Copy> = {
  en: {
    processing: "Processing your response…",
    processingShort: "Processing…",
    dealerFallback: "your dealer",
    device: "Lifeguard",
    accessEnds: "Access ends",
    approve: "Authorise access for 72 hours",
    reject: "Decline request",
    confirm: "Confirm",
    back: "Back",
    retry: "Try again",
    cancelAccess: "Cancel dealer access",
    confirmApprove: "Authorise diagnostic access for 72 hours?",
    confirmReject: "Decline this temporary access request?",
    confirmCancel: "Cancel the dealer’s temporary access now?",
    pendingTitle: "Temporary Lifeguard access request",
    pendingMessage: (dealer) => (
      <>
        You have received a temporary diagnostic access request for your
        Lifeguard from {dealer}. If you authorise it, access ends automatically
        after 72 hours.
      </>
    ),
    cancelTitle: "Cancel dealer access",
    cancelMessage: (dealer) => (
      <>
        Temporary diagnostic access to your Lifeguard is currently active for{" "}
        {dealer}. You can remove this access immediately. This action cannot be
        undone.
      </>
    ),
    approvedTitle: "Access authorised",
    alreadyApprovedTitle: "This request was already authorised",
    approvedMessage: (dealer) => (
      <>
        72-hour access to your Lifeguard has been successfully authorised for{" "}
        {dealer}.
      </>
    ),
    approvedHint:
      "If you change your mind, you can cancel this access at any time using the link in the confirmation email.",
    declinedTitle: "Request declined",
    declinedMessage: (dealer) => (
      <>
        The temporary access request from {dealer} has been declined. The dealer
        has not been given access to your Lifeguard.
      </>
    ),
    alreadyDeclinedTitle: "This request was already declined",
    alreadyDeclinedMessage: (dealer) => (
      <>
        The temporary access request from {dealer} was already declined, so
        access can no longer be authorised from this link. If the dealer still
        needs access, they can send a new request.
      </>
    ),
    requestExpiredTitle: "Request expired",
    requestExpiredMessage: (dealer) => (
      <>
        The temporary access request from {dealer} expired before it was
        answered. The dealer has not been given access to your Lifeguard.
      </>
    ),
    cancelledTitle: "Request cancelled",
    cancelledMessage:
      "This temporary access request has been cancelled and no longer needs a response.",
    revokedTitle: "Dealer access cancelled",
    revokedMessage: (dealer) => (
      <>Temporary access to your Lifeguard has been cancelled for {dealer}.</>
    ),
    accessExpiredTitle: "Access has ended",
    accessExpiredMessage: (dealer) => (
      <>The temporary access for {dealer} has already ended.</>
    ),
    invalidTitle: "Link not valid",
    invalidMessage:
      "This access link is invalid, expired, or no longer available.",
    errorTitle: "Something went wrong",
    errorMessage:
      "We couldn’t process your response. Please check your connection and try again.",
  },
  fr: {
    processing: "Traitement de votre réponse…",
    processingShort: "Traitement…",
    dealerFallback: "votre revendeur",
    device: "Lifeguard",
    accessEnds: "Fin de l’accès",
    approve: "Autoriser l’accès pour 72 h",
    reject: "Refuser la demande",
    confirm: "Confirmer",
    back: "Retour",
    retry: "Réessayer",
    cancelAccess: "Annuler l’accès du revendeur",
    confirmApprove: "Autoriser l’accès de diagnostic pour 72 heures ?",
    confirmReject: "Refuser cette demande d’accès temporaire ?",
    confirmCancel: "Annuler maintenant l’accès temporaire du revendeur ?",
    pendingTitle: "Demande d’accès temporaire à votre Lifeguard",
    pendingMessage: (dealer) => (
      <>
        Vous avez reçu une demande d’accès temporaire de diagnostic à votre
        Lifeguard de la part de {dealer}. En cas d’autorisation, l’accès
        expirera automatiquement après 72 heures.
      </>
    ),
    cancelTitle: "Annuler l’accès du revendeur",
    cancelMessage: (dealer) => (
      <>
        Un accès temporaire de diagnostic à votre Lifeguard est actuellement
        actif pour {dealer}. Vous pouvez supprimer cet accès immédiatement.
        Cette action est irréversible.
      </>
    ),
    approvedTitle: "Accès autorisé",
    alreadyApprovedTitle: "Cette demande a déjà été autorisée",
    approvedMessage: (dealer) => (
      <>
        L’accès de 72 heures à votre Lifeguard a bien été autorisé pour{" "}
        {dealer}.
      </>
    ),
    approvedHint:
      "Si vous changez d’avis, vous pouvez annuler cet accès à tout moment grâce au lien figurant dans l’e-mail de confirmation.",
    declinedTitle: "Demande refusée",
    declinedMessage: (dealer) => (
      <>
        La demande d’accès temporaire de {dealer} a été refusée. Le revendeur
        n’a pas accès à votre Lifeguard.
      </>
    ),
    alreadyDeclinedTitle: "Cette demande a déjà été refusée",
    alreadyDeclinedMessage: (dealer) => (
      <>
        La demande d’accès temporaire de {dealer} a déjà été refusée : l’accès
        ne peut plus être autorisé à partir de ce lien. Si le revendeur a
        toujours besoin d’un accès, il peut envoyer une nouvelle demande.
      </>
    ),
    requestExpiredTitle: "Demande expirée",
    requestExpiredMessage: (dealer) => (
      <>
        La demande d’accès temporaire de {dealer} a expiré avant d’avoir reçu
        une réponse. Le revendeur n’a pas accès à votre Lifeguard.
      </>
    ),
    cancelledTitle: "Demande annulée",
    cancelledMessage:
      "Cette demande d’accès temporaire a été annulée et ne nécessite plus de réponse.",
    revokedTitle: "Accès du revendeur annulé",
    revokedMessage: (dealer) => (
      <>L’accès temporaire à votre Lifeguard a été annulé pour {dealer}.</>
    ),
    accessExpiredTitle: "Accès terminé",
    accessExpiredMessage: (dealer) => (
      <>L’accès temporaire de {dealer} a déjà pris fin.</>
    ),
    invalidTitle: "Lien non valide",
    invalidMessage: "Ce lien est invalide, expiré ou n’est plus disponible.",
    errorTitle: "Une erreur est survenue",
    errorMessage:
      "Nous n’avons pas pu traiter votre réponse. Vérifiez votre connexion et réessayez.",
  },
};

const TONE_ICONS: Record<StatusTone, string> = {
  success: "pi-check",
  declined: "pi-times",
  info: "pi-info-circle",
  error: "pi-exclamation-triangle",
  prompt: "pi-shield",
};

interface OutcomeView {
  tone: StatusTone;
  title: string;
  message: React.ReactNode;
  hint?: string;
}

/**
 * Screen for a request that no longer awaits this customer. `action` is the
 * button the customer used, so a stale link explains what already happened.
 */
const outcomeView = (
  mode: Mode,
  status: TemporaryAccessStatus | undefined,
  action: DecisionAction | null,
  dealer: React.ReactNode,
  strings: Copy,
): OutcomeView => {
  switch (status) {
    case "active":
      return {
        tone: "success",
        title:
          action === "reject"
            ? strings.alreadyApprovedTitle
            : strings.approvedTitle,
        message: strings.approvedMessage(dealer),
        hint: strings.approvedHint,
      };
    case "rejected":
      return action === "approve"
        ? {
            tone: "declined",
            title: strings.alreadyDeclinedTitle,
            message: strings.alreadyDeclinedMessage(dealer),
          }
        : {
            tone: "declined",
            title: strings.declinedTitle,
            message: strings.declinedMessage(dealer),
          };
    case "revoked":
      return {
        tone: mode === "cancel" ? "success" : "info",
        title: strings.revokedTitle,
        message: strings.revokedMessage(dealer),
      };
    case "request_expired":
      return {
        tone: "info",
        title: strings.requestExpiredTitle,
        message: strings.requestExpiredMessage(dealer),
      };
    case "access_expired":
      return {
        tone: "info",
        title: strings.accessExpiredTitle,
        message: strings.accessExpiredMessage(dealer),
      };
    case "cancelled":
      return {
        tone: "info",
        title: strings.cancelledTitle,
        message: strings.cancelledMessage,
      };
    case "invalid":
    default:
      return {
        tone: "info",
        title: strings.invalidTitle,
        message: strings.invalidMessage,
      };
  }
};

/** Prefers the browser's regional variant of the page language. */
const formatDateTime = (value: string | null | undefined, locale: Locale) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const fallback = locale === "fr" ? "fr-FR" : "en-AU";
  const browser = typeof navigator === "undefined" ? "" : navigator.language;
  const options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  };
  try {
    const preferred = browser.toLowerCase().startsWith(locale)
      ? browser
      : fallback;
    return new Intl.DateTimeFormat(preferred, options).format(date);
  } catch {
    return new Intl.DateTimeFormat(fallback, options).format(date);
  }
};

const tokenStorageKey = (mode: Mode) => `temporary-access-token:${mode}`;

/**
 * Reads the signed token once. It is kept in sessionStorage (this tab only) so a
 * refresh still works after the token has been removed from the address bar.
 */
const readInitialToken = (mode: Mode, searchParams: URLSearchParams) => {
  const fromUrl = searchParams.get("token");
  try {
    if (fromUrl) {
      window.sessionStorage.setItem(tokenStorageKey(mode), fromUrl);
      return fromUrl;
    }
    return window.sessionStorage.getItem(tokenStorageKey(mode)) || "";
  } catch {
    return fromUrl || "";
  }
};

/** Drops the bearer token from the address bar and browser history entry. */
const removeTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("token")) return;
    url.searchParams.delete("token");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // Non-critical: the page still works with the token in the URL.
  }
};

/**
 * Public landing page for the customer email buttons. It never requires a
 * portal session: the signed token in the link is the only credential.
 */
const TemporaryAccessDecision: React.FC<TemporaryAccessDecisionProps> = ({
  mode,
}) => {
  const [searchParams] = useSearchParams();
  const [token] = useState(() => readInitialToken(mode, searchParams));
  const emailAction = searchParams.get("action");
  const emailDecision: DecisionAction | null =
    mode === "decision" &&
    (emailAction === "approve" || emailAction === "reject")
      ? emailAction
      : null;

  const decisionState = usePublicTemporaryAccessDecision(
    token,
    mode === "decision",
  );
  const cancelState = usePublicTemporaryAccessCancel(token, mode === "cancel");
  const stateQuery = mode === "decision" ? decisionState : cancelState;
  const submitDecision = useSubmitTemporaryAccessDecision();
  const submitCancel = useSubmitTemporaryAccessCancel();
  const { mutateAsync: decide } = submitDecision;

  const [confirmation, setConfirmation] = useState<
    DecisionAction | "cancel" | null
  >(null);
  const [chosenAction, setChosenAction] = useState<DecisionAction | null>(null);
  const [completed, setCompleted] = useState<CompletedResult | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const preselectedEmailAction = useRef(false);
  const confirmationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    removeTokenFromUrl();
  }, []);

  const state = stateQuery.data;
  const locale: Locale = state?.locale === "fr" ? "fr" : "en";
  const strings = copy[locale];
  const status =
    completed?.status ?? state?.status ?? (token ? undefined : "invalid");
  const pending = mode === "decision" && status === "pending";
  const cancellable = mode === "cancel" && status === "active";
  const processing = submitDecision.isPending || submitCancel.isPending;
  const loadFailed = Boolean(token) && !completed && stateQuery.isError;
  const loading =
    Boolean(token) &&
    !completed &&
    (stateQuery.isLoading || (loadFailed && stateQuery.isFetching));
  const busy = loading;
  const failed = !busy && (submitFailed || loadFailed);
  const action = chosenAction ?? emailDecision;

  // The email button only pre-selects the choice. Opening the link never
  // submits on its own, so mail scanners that run JavaScript cannot approve
  // or decline on the customer's behalf; a Confirm click is always required.
  useEffect(() => {
    if (!emailDecision || !pending || preselectedEmailAction.current) return;
    preselectedEmailAction.current = true;
    setConfirmation(emailDecision);
  }, [emailDecision, pending]);

  // The clicked button disappears, so move focus to the confirmation prompt.
  useEffect(() => {
    if (confirmation) confirmationRef.current?.focus();
  }, [confirmation]);

  const retry = () => {
    setSubmitFailed(false);
    void stateQuery.refetch();
  };

  const commit = async () => {
    if (!confirmation || !token) return;
    const chosen = confirmation;
    try {
      if (chosen === "cancel") {
        setCompleted(await submitCancel.mutateAsync(token));
      } else {
        setChosenAction(chosen);
        setCompleted(await decide({ token, decision: chosen }));
      }
    } catch {
      setSubmitFailed(true);
    } finally {
      setConfirmation(null);
    }
  };

  const dealerName = state?.dealerName?.trim();
  const dealer = dealerName ? <strong>{dealerName}</strong> : strings.dealerFallback;
  const accessEnds =
    status === "active"
      ? formatDateTime(
          completed?.accessExpiresAt ?? state?.accessExpiresAt,
          locale,
        )
      : null;

  const details =
    state?.serialNumber || accessEnds ? (
      <Details>
        {state?.serialNumber && (
          <DetailRow>
            <dt>{strings.device}</dt>
            <dd>{state.serialNumber}</dd>
          </DetailRow>
        )}
        {accessEnds && (
          <DetailRow>
            <dt>{strings.accessEnds}</dt>
            <dd>{accessEnds}</dd>
          </DetailRow>
        )}
      </Details>
    ) : null;

  const statusIcon = (tone: StatusTone, icon = TONE_ICONS[tone]) => (
    <StatusIcon $tone={tone}>
      <i className={`pi ${icon}`} aria-hidden="true" />
    </StatusIcon>
  );

  const confirmationPrompt = confirmation && (
    <Confirmation ref={confirmationRef} tabIndex={-1}>
      <p>
        {confirmation === "approve"
          ? strings.confirmApprove
          : confirmation === "reject"
            ? strings.confirmReject
            : strings.confirmCancel}
      </p>
      <Actions>
        <ActionButton
          type="button"
          $variant="secondary"
          onClick={() => setConfirmation(null)}
          disabled={processing}
        >
          {strings.back}
        </ActionButton>
        <ActionButton
          type="button"
          $variant={confirmation === "approve" ? "primary" : "danger"}
          onClick={commit}
          disabled={processing}
        >
          {processing ? strings.processingShort : strings.confirm}
        </ActionButton>
      </Actions>
    </Confirmation>
  );

  let content: React.ReactNode;
  if (busy) {
    content = (
      <ProcessingState role="status">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
        <p>{strings.processing}</p>
      </ProcessingState>
    );
  } else if (failed) {
    content = (
      <>
        {statusIcon("error")}
        <Title>{strings.errorTitle}</Title>
        <Message>{strings.errorMessage}</Message>
        <Actions>
          <ActionButton type="button" $variant="primary" onClick={retry}>
            {strings.retry}
          </ActionButton>
        </Actions>
      </>
    );
  } else if (pending) {
    content = (
      <>
        {statusIcon("prompt")}
        <Title>{strings.pendingTitle}</Title>
        <Message>{strings.pendingMessage(dealer)}</Message>
        {details}
        {confirmationPrompt || (
          <Actions>
            <ActionButton
              type="button"
              $variant="primary"
              onClick={() => setConfirmation("approve")}
            >
              {strings.approve}
            </ActionButton>
            <ActionButton
              type="button"
              $variant="secondary"
              onClick={() => setConfirmation("reject")}
            >
              {strings.reject}
            </ActionButton>
          </Actions>
        )}
      </>
    );
  } else if (cancellable) {
    content = (
      <>
        {statusIcon("prompt", "pi-lock-open")}
        <Title>{strings.cancelTitle}</Title>
        <Message>{strings.cancelMessage(dealer)}</Message>
        {details}
        {confirmationPrompt || (
          <Actions>
            <ActionButton
              type="button"
              $variant="danger"
              onClick={() => setConfirmation("cancel")}
            >
              {strings.cancelAccess}
            </ActionButton>
          </Actions>
        )}
      </>
    );
  } else {
    const view = outcomeView(mode, status, action, dealer, strings);
    content = (
      <>
        {statusIcon(view.tone)}
        <Title>{view.title}</Title>
        <Message>{view.message}</Message>
        {details}
        {view.hint && <Hint>{view.hint}</Hint>}
      </>
    );
  }

  return (
    <PageContainer lang={locale}>
      <PageHeader>
        <HeaderLogo src={daveyLogo} alt="Davey" />
      </PageHeader>
      <PageMain>
        <Card aria-live="polite" aria-busy={busy}>
          {content}
        </Card>
      </PageMain>
      <PageFooter>© {new Date().getFullYear()} Davey Water</PageFooter>
    </PageContainer>
  );
};

export default TemporaryAccessDecision;
